import { Injectable, inject } from '@angular/core';
import {
	ILifeTest,
	ILifeTestApproachFaceScoring,
	ILifeTestGeneralConf,
	ILifeTestGeneralScoring,
	ILifeTestMiddleFaceScoring,
	ILifeTestScoring,
	IMiddleFace,
	INoseMovementValues,
	IPolConf,
	IPoLScoring,
	IScoringRange,
	ISimilarity,
	LIFE_TEST_ID,
	SCORING_PARAMETER,
} from '../../models/life-check';
import { Category, Classifications } from '@mediapipe/tasks-vision';
import { scoring } from '../../components/life-check/life-check-scoring';
import { PositionFaceService } from './position-face.service';
import { IPoint } from '../../models/user-identification';
import {
	DEFAULT_FACE_DEPTH_REAL_THRESHOLDS,
	FaceDepthRealScores,
} from 'src/app/models/services/face-depth-real.models';

@Injectable({
	providedIn: 'root',
})
export class ScoringFaceService {
	/** Deciles de transición en pruebas distintas de sonrisa (comportamiento histórico). */
	private static readonly LIFE_TEST_TRANSITION_DECILE_PCT = 10;

	/** Escala 0–100 de la barra de progreso de validación (NIF-385). */
	private static readonly LIFE_TEST_PROGRESS_SCALE_PCT = 100;
	/** Tope de barra antes del check verde; el 100 % lo marca la animación de OK. */
	private static readonly LIFE_TEST_PROGRESS_MAX_BEFORE_VALIDATION = 99;
	private static readonly LIFE_TEST_PROGRESS_RATIO_CAP = 1;
	private static readonly LIFE_TEST_PROGRESS_MIN_DIVISOR = 1;
	private static readonly LIFE_TEST_PROGRESS_MS_PER_SECOND = 1000;
	private static readonly LIFE_TEST_PROGRESS_WEIGHT_EMOTION = 0.35;
	private static readonly LIFE_TEST_PROGRESS_WEIGHT_TRANSITION = 0.35;
	private static readonly LIFE_TEST_PROGRESS_WEIGHT_CENTERED = 0.2;
	private static readonly LIFE_TEST_PROGRESS_WEIGHT_TIME = 0.1;
	private static readonly DEFAULT_SIMILARITY: ISimilarity = { environment: 0, face: 0 };

	private fullScoring: IPoLScoring = scoring;

	// Middle face
	private fullTestMatch: Array<boolean> = [];
	private lifeTestMatch: Array<boolean> = [];
	private validationMatch: Array<boolean> = [];

	// Similarity
	private similarity: ISimilarity = { ...ScoringFaceService.DEFAULT_SIMILARITY };

	// Emoción
	private fullEmotions: Array<number> = [];
	private lifeTestEmotion: Array<number> = [];
	private validationEmotion: Array<number> = [];

	// Parpadeo
	private totalBlinks: number = 0;
	private countBlink: number = 0;
	private MIN_MEASURE_FOR_BLINK: number = 3;
	private hasBlink: boolean = false;

	private totalBlinksWith2: number = 0;
	private countBlink2: number = 0;
	private hasBlink2: boolean = false;
	private totalBlinksWith1: number = 0;
	private hasBlink1: boolean = false;
	private countBlink1: number = 0;

	// Número de caras simultaneas
	private simultaneousFaces: number = 1;

	// Movimiento de la nariz
	private noseMovement: INoseMovementValues;
	private positionFaceService = inject(PositionFaceService);

	/*
	 * General
	 */
	public calcGeneralScoring(
		lifeCheckConf: IPolConf,
		totalTime: number,
		percentageSimilarity: {
			similarity?: Array<ISimilarity>;
			result: number;
		},
	): void {
		if (!lifeCheckConf.general.scoring) {
			lifeCheckConf.general.scoring = {} as ILifeTestGeneralScoring;
		}
		lifeCheckConf.general.scoring[SCORING_PARAMETER.Emotion] = this.getCoefficientVariation();
		lifeCheckConf.general.scoring[SCORING_PARAMETER.BlinksByMin] = this.getAverageBlinks(
			this.totalBlinks,
			totalTime,
		);
		lifeCheckConf.general.scoring[SCORING_PARAMETER.BlinksByMinV2] = this.getAverageBlinks(
			this.totalBlinksWith2,
			totalTime,
		);
		lifeCheckConf.general.scoring[SCORING_PARAMETER.BlinksByMinV1] = this.getAverageBlinks(
			this.totalBlinksWith1,
			totalTime,
		);
		lifeCheckConf.general.scoring[SCORING_PARAMETER.SimilarityPercentage] = percentageSimilarity.result;
		lifeCheckConf.general.scoring[SCORING_PARAMETER.SimilarityPercentageSeparate] = percentageSimilarity.similarity;
		lifeCheckConf.general.scoring[SCORING_PARAMETER.PercentageTimeInTheMiddle] = this.getPercentageTimeInMiddle(
			this.fullTestMatch,
		);
		lifeCheckConf.general.scoring[SCORING_PARAMETER.TotalTries] = lifeCheckConf.general.countTries;
		lifeCheckConf.general.scoring[SCORING_PARAMETER.TotalTime] = totalTime;
		lifeCheckConf.general.scoring[SCORING_PARAMETER.LifeTests] = this.getAverageLifeTestResult(lifeCheckConf);
		lifeCheckConf.general.scoring[SCORING_PARAMETER.Result] = this.getScoringResult(
			LIFE_TEST_ID.GENERAL,
			lifeCheckConf.general.scoring,
			lifeCheckConf,
		);
		lifeCheckConf.general.scoring[SCORING_PARAMETER.Photos] = this.getLabelsPhotos(lifeCheckConf);
		// console.log(`General scoring: ${JSON.stringify(lifeCheckConf.general.scoring)}`);
	}

	/*
	 * Middleface
	 */
	public calcMiddleFaceScoring(middleFace: IMiddleFace, isFinish: boolean): void {
		const scoring: ILifeTestMiddleFaceScoring = {} as ILifeTestMiddleFaceScoring;
		scoring[SCORING_PARAMETER.PercentageTimeInTheMiddle] = this.getPercentageTimeInMiddle(
			isFinish ? this.lifeTestMatch : this.validationMatch,
		);
		scoring[SCORING_PARAMETER.SimilarityPercentage] = this.getAverageSimilarityPercent();
		scoring[SCORING_PARAMETER.TotalTime] =
			((middleFace.finishAt ? new Date(middleFace.finishAt).getTime() : new Date().getTime()) -
				new Date(middleFace.startAt).getTime()) /
			1000;
		scoring[SCORING_PARAMETER.TotalTries] = 1;
		scoring[SCORING_PARAMETER.Result] = this.getScoringResult(middleFace.lifeTestId, scoring);

		if (isFinish) {
			if (!middleFace.scoring) {
				middleFace.scoring = {} as ILifeTestMiddleFaceScoring;
			}
			middleFace.scoring = scoring;
			this.lifeTestMatch = [];
			// console.log(`Middle face scoring: ${JSON.stringify(middleFace.scoring)}`);
		} else {
			if (!middleFace.fullScoring) {
				middleFace.fullScoring = [];
			}
			middleFace.fullScoring.push(scoring);
		}
	}

	/*
	 * Pruebas de vida
	 */
	public calcLifeTestScoring(lifeTest: ILifeTest, isFinish: boolean): void {
		const scoring: ILifeTestScoring = {} as ILifeTestScoring;
		scoring[SCORING_PARAMETER.Emotion] = this.getPercentageLifeTestEmotion(
			isFinish ? this.lifeTestEmotion : this.validationEmotion,
		);
		scoring[SCORING_PARAMETER.MaxEmotion] = this.getLifeTestMaxEmotion(this.lifeTestEmotion);
		scoring[SCORING_PARAMETER.EmotionTransition] = this.getLifeTestTransition(this.lifeTestEmotion);
		scoring[SCORING_PARAMETER.SimilarityPercentage] = this.getAverageSimilarityPercent();
		scoring[SCORING_PARAMETER.PercentageTimeInTheMiddle] = this.getPercentageTimeInMiddle(
			isFinish ? this.lifeTestMatch : this.validationMatch,
		);
		scoring[SCORING_PARAMETER.TotalTime] =
			((lifeTest.finishAt ? new Date(lifeTest.finishAt).getTime() : new Date().getTime()) -
				new Date(lifeTest.startAt).getTime()) /
			1000;
		scoring[SCORING_PARAMETER.TotalTries] = 1;
		if (this.noseMovement) {
			scoring[SCORING_PARAMETER.NoseDistance] = this.noseMovement[SCORING_PARAMETER.NoseDistance];
			scoring[SCORING_PARAMETER.NoseTransition] = this.noseMovement[SCORING_PARAMETER.NoseTransition];
			scoring[SCORING_PARAMETER.NoseVariance] = this.noseMovement[SCORING_PARAMETER.NoseVariance];
		}
		scoring[SCORING_PARAMETER.Result] = this.getScoringResult(lifeTest.lifeTestId, scoring);

		if (isFinish) {
			if (!lifeTest.scoring) {
				lifeTest.scoring = {} as ILifeTestScoring;
			}
			lifeTest.scoring = scoring;
			this.lifeTestMatch = [];
			this.lifeTestEmotion = [];
			// console.log(`${lifeTest.lifeTestId} scoring: ${JSON.stringify(lifeTest.scoring)}`);
		} else {
			if (!lifeTest.fullScoring) {
				lifeTest.fullScoring = [];
			}
			lifeTest.fullScoring.push(scoring);
		}
	}

	/**
	 * Scoring approach-face (NIF-739): parámetros del lab → result 0–10 vía rangos/pesos.
	 * TotalTries = 1 (mismo criterio que smile/turns en la subprueba).
	 */
	public calcApproachFaceScoring(
		lifeTest: ILifeTest,
		scores: FaceDepthRealScores,
		percentageTimeInMiddle: number,
		isFinish: boolean,
		depthDeltaRange?: { minDeltaDepthM: number; maxDeltaDepthM: number },
	): void {
		const scoring = {} as ILifeTestApproachFaceScoring;
		scoring[SCORING_PARAMETER.FinalScore] = scores.finalScore;
		scoring[SCORING_PARAMETER.ShapeScore] = scores.shapeScore;
		scoring[SCORING_PARAMETER.AppearanceScore] = scores.appearanceScore;
		scoring[SCORING_PARAMETER.DepthDelta] = this.normalizeApproachDepthDelta(
			scores.deltaDepthM,
			depthDeltaRange?.minDeltaDepthM,
			depthDeltaRange?.maxDeltaDepthM,
		);
		scoring[SCORING_PARAMETER.PercentageTimeInTheMiddle] = percentageTimeInMiddle;
		scoring[SCORING_PARAMETER.TotalTime] =
			((lifeTest.finishAt ? new Date(lifeTest.finishAt).getTime() : new Date().getTime()) -
				new Date(lifeTest.startAt).getTime()) /
			1000;
		scoring[SCORING_PARAMETER.TotalTries] = 1;
		scoring[SCORING_PARAMETER.Result] = this.getScoringResult(LIFE_TEST_ID.APPROACHFACE, scoring);

		if (isFinish) {
			lifeTest.scoring = scoring;
			this.lifeTestMatch = [];
		} else {
			if (!lifeTest.fullScoring) {
				lifeTest.fullScoring = [];
			}
			lifeTest.fullScoring.push(scoring);
		}
	}

	/**
	 * Normaliza deltaDepthM al rango lab [min, max] → 0–100 (óptimo en el centro; fuera → 0).
	 */
	public normalizeApproachDepthDelta(
		deltaDepthM: number,
		minDelta: number = DEFAULT_FACE_DEPTH_REAL_THRESHOLDS.minDeltaDepthM,
		maxDelta: number = DEFAULT_FACE_DEPTH_REAL_THRESHOLDS.maxDeltaDepthM,
	): number {
		if (!(deltaDepthM >= minDelta && deltaDepthM <= maxDelta)) {
			return 0;
		}
		const mid = (minDelta + maxDelta) / 2;
		const half = Math.max(1e-6, (maxDelta - minDelta) / 2);
		const score = (1 - Math.abs(deltaDepthM - mid) / half) * 100;
		return Math.max(0, Math.min(100, score));
	}

	/*
	 * Funciones para calcular resultados
	 */
	// Obtenemos el scoring de la prueba en %
	private getScoringResult(
		testId: LIFE_TEST_ID,
		scoring:
			| ILifeTestMiddleFaceScoring
			| ILifeTestScoring
			| ILifeTestApproachFaceScoring
			| ILifeTestGeneralScoring,
		lifeCheckConf?: IPolConf,
	): number {
		const values: { value: number; weight: number }[] = [];
		let totalWeight: number = 0;
		let totalValue: number = 0;
		for (const parameter in scoring) {
			if (
				this.fullScoring &&
				this.fullScoring[testId] &&
				this.fullScoring[testId][parameter] &&
				this.fullScoring[testId][parameter].ranges &&
				scoring[parameter] !== null &&
				scoring[parameter] !== undefined
			) {
				const valueParameter: number = scoring[parameter];
				const weight: number = this.fullScoring[testId][parameter].weight;
				totalWeight += weight;
				// Modificamos los rangos  del tiempo total para adaptarlos a cada intento o número de pruebas
				if (parameter === SCORING_PARAMETER.TotalTime) {
					if (testId === LIFE_TEST_ID.GENERAL) {
						// El +1 es para incluir la fase del centrado
						this.fullScoring[testId][parameter].ranges.map((r: IScoringRange) => {
							if (r.min) {
								r.min =
									r.min *
									(scoring as ILifeTestGeneralScoring).totalTries *
									this.getNumberOfTest(lifeCheckConf);
							}
							if (r.max) {
								r.max =
									r.max *
									(scoring as ILifeTestGeneralScoring).totalTries *
									this.getNumberOfTest(lifeCheckConf);
							}
						});
					} else {
						// Modificamos los rangos para ajustarlos a cada prueba
						this.fullScoring[testId][parameter].ranges.map((r: IScoringRange) => {
							const tries =
								(scoring as ILifeTestMiddleFaceScoring | ILifeTestScoring | ILifeTestApproachFaceScoring)[
									SCORING_PARAMETER.TotalTries
								] ?? 1;
							if (r.min) {
								r.min = r.min * tries;
							}
							if (r.max) {
								r.max = r.max * tries;
							}
						});
					}
				}
				const found = this.fullScoring[testId][parameter].ranges.find((r: IScoringRange) =>
					valueParameter > r.min && r.max !== null ? valueParameter <= r.max : true,
				);
				if (found) {
					values.push({ value: found.value || 0, weight: weight });
				} else {
					if (parameter === SCORING_PARAMETER.LifeTests) {
						values.push({ value: valueParameter, weight: weight });
					} else {
						values.push({ value: 0, weight: weight });
						// console.log(`No se encuentra rango: ${testId} - ${parameter}`);
					}
				}
			} else {
				// console.log(`No encontramos el valor en el scoring o valor generado: ${testId} - ${parameter}`);
			}
		}
		values.forEach((v: { value: number; weight: number }) => {
			const x = (v.weight * 100) / totalWeight;
			totalValue += (x * v.value) / 10;
		});
		return totalValue / 10;
	}

	/** Fases de un intento completo: centrado + N pruebas de vida configuradas (sin reintentos por fase). */
	private getNumberOfTest(lifeCheckConf: IPolConf): number {
		return 1 + lifeCheckConf.general.totalLifeTest;
	}

	// Porcentaje de tiempo en medio
	public getPercentageTimeInMiddle(matchs: Array<boolean>, controls?: any): number {
		const counts = {};
		let result: number = 0;
		if (!matchs.length) return result;
		matchs.forEach(function (x: any) {
			counts[x] = (counts[x] || 0) + 1;
		});
		result = counts['true'] ? (counts['true'] * 100) / matchs.length : 0;
		if (controls) {
			controls.percentageTimeInCamera = result;
		}
		return result;
	}

	// Porcentage medio de toas las emociones
	public saveGeneralEmotions(faceBlendshapes: Classifications[]): void {
		if (faceBlendshapes?.length && faceBlendshapes[0].categories) {
			const sum = faceBlendshapes[0].categories.reduce((acc: number, obj: Category) => acc + obj.score * 100, 0);
			this.fullEmotions.push(sum / faceBlendshapes[0].categories.length);
		}
	}

	// Obtenemos el coeeficiente de variación sobre una muestra
	private getCoefficientVariation(): number {
		const media = this.fullEmotions.reduce((acc: number, num: number) => acc + num, 0) / this.fullEmotions.length;
		const sumaDiferenciasCuadradas = this.fullEmotions.reduce(
			(acc: number, num: number) => acc + Math.pow(num - media, 2),
			0,
		);
		const varianza = sumaDiferenciasCuadradas / this.fullEmotions.length;
		const desviacionEstandar = Math.sqrt(varianza);
		return (desviacionEstandar / media) * 100;
	}

	// Porcentaje de emocion superior al de la prueba
	public getPercentageLifeTestEmotion(inputEmotions: Array<any>, controls?: any): number {
		if (inputEmotions.length) {
			const averageArray: Array<number> = this.getAverageArray(inputEmotions);
			if (controls) {
				controls.percentageLifeTest =
					averageArray.reduce((partialSum, a) => partialSum + a, 0) / averageArray.length;
			}
			if (averageArray.length) {
				return averageArray.reduce((partialSum, a) => partialSum + a, 0) / averageArray.length;
			}
		}
		return 0;
	}

	// Transiciones de la prueba de vida
	public getLifeTestTransition(inputEmotions: Array<any>, controls?: any): number {
		if (!inputEmotions.length) {
			return 0;
		}
		const averageArray = this.getAverageArray(inputEmotions);
		const rounded: number[] = [];
		const decilePct = ScoringFaceService.LIFE_TEST_TRANSITION_DECILE_PCT;
		for (const i of averageArray) {
			rounded.push(i - (i % decilePct));
		}
		const count = Array.from(new Set(rounded)).length;
		if (controls) {
			controls.percentageTransition = count;
		}
		return count;
	}

	/**
	 * Progreso 0–99 alineado con los mismos criterios que `isLifeTestValid` (UI honesta, NIF-385).
	 */
	public getLifeTestValidationProgress(
		lifeTest: ILifeTest,
		general: ILifeTestGeneralConf,
		validationStartedAtMs: number | null,
	): number {
		const emotionPct = this.getPercentageLifeTestEmotion(this.validationEmotion);
		const transitions = this.getLifeTestTransition(this.lifeTestEmotion);
		const centeredPct = this.getPercentageTimeInMiddle(this.validationMatch);

		const minDivisor = ScoringFaceService.LIFE_TEST_PROGRESS_MIN_DIVISOR;
		const ratioCap = ScoringFaceService.LIFE_TEST_PROGRESS_RATIO_CAP;
		const emotionRatio = Math.min(
			ratioCap,
			emotionPct / Math.max(lifeTest.minPercentageFaceBlendshape, minDivisor),
		);
		const transitionRatio = Math.min(
			ratioCap,
			transitions / Math.max(lifeTest.minTransitionsFaceBlendshape, minDivisor),
		);
		const centerRatio = Math.min(ratioCap, centeredPct / Math.max(general.minPercenteageTimeInMiddle, minDivisor));
		let timeRatio = 0;
		if (validationStartedAtMs != null && lifeTest.minTime > 0) {
			timeRatio = Math.min(
				ratioCap,
				(Date.now() - validationStartedAtMs) /
					(lifeTest.minTime * ScoringFaceService.LIFE_TEST_PROGRESS_MS_PER_SECOND),
			);
		}

		const composite =
			(emotionRatio * ScoringFaceService.LIFE_TEST_PROGRESS_WEIGHT_EMOTION +
				transitionRatio * ScoringFaceService.LIFE_TEST_PROGRESS_WEIGHT_TRANSITION +
				centerRatio * ScoringFaceService.LIFE_TEST_PROGRESS_WEIGHT_CENTERED +
				timeRatio * ScoringFaceService.LIFE_TEST_PROGRESS_WEIGHT_TIME) *
			ScoringFaceService.LIFE_TEST_PROGRESS_SCALE_PCT;
		return Math.round(
			Math.min(ScoringFaceService.LIFE_TEST_PROGRESS_MAX_BEFORE_VALIDATION, Math.max(0, composite)),
		);
	}

	// Devuelve el valor más alto de un array de objetos
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private getLifeTestMaxEmotion(inputEmotions: Array<any>, controls?: any): number {
		if (inputEmotions.length) {
			return Math.max(...this.getAverageArray(inputEmotions));
		}
		return 0;
	}

	// Control del movimiento de la nariz
	public getNoseMovement(lifeTest: ILifeTest, nosePath: Array<IPoint>, controls?: any): INoseMovementValues {
		// - Un % de puntos no se pueden salir de ese recuadro
		// - Desde el punto de la nariz hasta el punto mas alejado registrado debemos hacer intervalos de X px y establecer de cuantos intervalos tenemos valor
		// - Establecer un % de puntos válido para validar la transición
		const emptyNoseMovement = (): INoseMovementValues => ({
			[SCORING_PARAMETER.NoseDistance]: 0,
			[SCORING_PARAMETER.NoseTransition]: 0,
			[SCORING_PARAMETER.NoseVariance]: 0,
		});
		const isHeadMovementTest =
			lifeTest.lifeTestId === LIFE_TEST_ID.TURNRIGHTHEAD ||
			lifeTest.lifeTestId === LIFE_TEST_ID.TURNLEFTHEAD ||
			lifeTest.lifeTestId === LIFE_TEST_ID.LIFTHEAD ||
			lifeTest.lifeTestId === LIFE_TEST_ID.LOWERHEAD;
		const noseCenterPoint = this.positionFaceService.getNoseCenterPoint();
		if (isHeadMovementTest && !noseCenterPoint) {
			this.noseMovement = emptyNoseMovement();
			return this.noseMovement;
		}

		let aux: number = 0;
		nosePath.forEach((nosePoint: IPoint) => {
			if (this.positionFaceService.isPointInRect(nosePoint, this.positionFaceService.getNoseControlRect())) {
				aux++;
			}
		});
		const percentPointInRect = nosePath.length ? (aux * 100) / nosePath.length : 0;

		let distance: number = 0;
		const rounded: Array<number> = [];
		switch (lifeTest.lifeTestId) {
			case LIFE_TEST_ID.TURNRIGHTHEAD:
				distance = Math.abs(
					this.positionFaceService.getNoseCenterPoint().x -
						Math.min(
							...nosePath
								.map((nosePoint: IPoint) => {
									return this.positionFaceService.isPointInRect(
										nosePoint,
										this.positionFaceService.getNoseControlRect(),
									)
										? nosePoint.x
										: null;
								})
								.filter((f) => f),
						),
				);
				nosePath.forEach((nosePoint: IPoint) => {
					// Si tenemos un punto dentro del cuadrado y que esté entre el punto de control de la nariz y el de máxima distancia
					if (
						this.positionFaceService.isPointInRect(
							nosePoint,
							this.positionFaceService.getNoseControlRect(),
						) &&
						nosePoint.x < this.positionFaceService.getNoseCenterPoint().x &&
						nosePoint.x > this.positionFaceService.getNoseCenterPoint().x - distance
					) {
						const percent =
							(Math.abs(this.positionFaceService.getNoseCenterPoint().x - nosePoint.x) * 100) / distance;
						rounded.push(percent - (percent % 10));
					}
				});
				break;
			case LIFE_TEST_ID.TURNLEFTHEAD:
				distance = Math.abs(
					this.positionFaceService.getNoseCenterPoint().x -
						Math.max(
							...nosePath
								.map((nosePoint: IPoint) => {
									return this.positionFaceService.isPointInRect(
										nosePoint,
										this.positionFaceService.getNoseControlRect(),
									)
										? nosePoint.x
										: null;
								})
								.filter((f) => f),
						),
				);
				nosePath.forEach((nosePoint: IPoint) => {
					// Si tenemos un punto dentro del cuadrado y que esté entre el punto de control de la nariz y el de máxima distancia
					if (
						this.positionFaceService.isPointInRect(
							nosePoint,
							this.positionFaceService.getNoseControlRect(),
						) &&
						nosePoint.x > this.positionFaceService.getNoseCenterPoint().x &&
						nosePoint.x < this.positionFaceService.getNoseCenterPoint().x + distance
					) {
						const percent =
							(Math.abs(this.positionFaceService.getNoseCenterPoint().x - nosePoint.x) * 100) / distance;
						rounded.push(percent - (percent % 10));
					}
				});
				break;
			case LIFE_TEST_ID.LIFTHEAD:
				distance = Math.abs(
					this.positionFaceService.getNoseCenterPoint().y -
						Math.min(
							...nosePath
								.map((nosePoint: IPoint) => {
									return this.positionFaceService.isPointInRect(
										nosePoint,
										this.positionFaceService.getNoseControlRect(),
									)
										? nosePoint.y
										: null;
								})
								.filter((f) => f),
						),
				);
				nosePath.forEach((nosePoint: IPoint) => {
					// Si tenemos un punto dentro del cuadrado y que esté entre el punto de control de la nariz y el de máxima distancia
					if (
						this.positionFaceService.isPointInRect(
							nosePoint,
							this.positionFaceService.getNoseControlRect(),
						) &&
						nosePoint.y < this.positionFaceService.getNoseCenterPoint().y &&
						nosePoint.y > this.positionFaceService.getNoseCenterPoint().y - distance
					) {
						const percent =
							(Math.abs(this.positionFaceService.getNoseCenterPoint().y - nosePoint.y) * 100) / distance;
						rounded.push(percent - (percent % 10));
					}
				});
				break;
			case LIFE_TEST_ID.LOWERHEAD:
				distance = Math.abs(
					this.positionFaceService.getNoseCenterPoint().y -
						Math.max(
							...nosePath
								.map((nosePoint: IPoint) => {
									return this.positionFaceService.isPointInRect(
										nosePoint,
										this.positionFaceService.getNoseControlRect(),
									)
										? nosePoint.y
										: null;
								})
								.filter((f) => f),
						),
				);
				nosePath.forEach((nosePoint: IPoint) => {
					// Si tenemos un punto dentro del cuadrado y que esté entre el punto de control de la nariz y el de máxima distancia
					if (
						this.positionFaceService.isPointInRect(
							nosePoint,
							this.positionFaceService.getNoseControlRect(),
						) &&
						nosePoint.y > this.positionFaceService.getNoseCenterPoint().y &&
						nosePoint.y < this.positionFaceService.getNoseCenterPoint().y + distance
					) {
						const percent =
							(Math.abs(this.positionFaceService.getNoseCenterPoint().y - nosePoint.y) * 100) / distance;
						rounded.push(percent - (percent % 10));
					}
				});
				break;
			default:
				this.noseMovement = {
					[SCORING_PARAMETER.NoseDistance]: 0,
					[SCORING_PARAMETER.NoseTransition]: 0,
					[SCORING_PARAMETER.NoseVariance]: 0,
				};
				return this.noseMovement;
		}
		if (controls) {
			controls.noseMovement = {
				percentageVariance: percentPointInRect,
				distance: distance,
				transitions: Array.from(new Set(rounded)).length,
			};
		}
		this.noseMovement = {
			[SCORING_PARAMETER.NoseDistance]: distance,
			[SCORING_PARAMETER.NoseTransition]: Array.from(new Set(rounded)).length,
			[SCORING_PARAMETER.NoseVariance]: percentPointInRect,
		};
		return this.noseMovement;
	}

	// Control de movimientos de la nariz por prueba
	/**
	 * Distancia de nariz del frame actual ≥ `minDistance` (giros L/R).
	 * Usado para no acumular blendshapes de validación si ojos y nariz no van a la vez (NIF-644).
	 */
	public isInstantTurnNoseDistanceMet(lifeTest: ILifeTest): boolean {
		if (
			lifeTest.lifeTestId !== LIFE_TEST_ID.TURNLEFTHEAD &&
			lifeTest.lifeTestId !== LIFE_TEST_ID.TURNRIGHTHEAD
		) {
			return true;
		}
		const minDistance = lifeTest.noseMovement?.minDistance;
		if (minDistance == null) {
			return true;
		}
		const nosePoint = this.positionFaceService.getNosePoint();
		const noseCenterPoint = this.positionFaceService.getNoseCenterPoint();
		if (!nosePoint || !noseCenterPoint) {
			return false;
		}
		const distance =
			lifeTest.lifeTestId === LIFE_TEST_ID.TURNRIGHTHEAD
				? noseCenterPoint.x - nosePoint.x
				: nosePoint.x - noseCenterPoint.x;
		return distance >= minDistance;
	}

	public areHeadMovementsValid(lifeTest: ILifeTest, nosePath: Array<IPoint>, controls?: any): boolean {
		if (lifeTest) {
			switch (lifeTest.lifeTestId) {
				case LIFE_TEST_ID.TURNRIGHTHEAD:
				case LIFE_TEST_ID.TURNLEFTHEAD:
				case LIFE_TEST_ID.LIFTHEAD:
				case LIFE_TEST_ID.LOWERHEAD:
					// eslint-disable-next-line no-case-declarations
					const noseValues: INoseMovementValues = this.getNoseMovement(lifeTest, nosePath, controls);
					return (
						noseValues[SCORING_PARAMETER.NoseVariance] >
							(lifeTest.noseMovement?.minPercentageVariance || 70) &&
						noseValues[SCORING_PARAMETER.NoseDistance] > (lifeTest.noseMovement?.minDistance || 40) &&
						noseValues[SCORING_PARAMETER.NoseTransition] >= (lifeTest.noseMovement?.minTransitions || 7)
					);
				default:
					return true;
			}
		}
		return false;
	}

	// Conteo de parpadeos
	public thereIsSomeBlink(
		result: { eyeBlinkLeft: number; eyeBlinkRight: number },
		minPercenteageBlink: number,
		controls?: any,
	): void {
		if (result) {
			let averageValue: number = 0;
			for (const field of Object.keys(result)) {
				averageValue += result[field] * 100 || 0;
			}
			if (Object.keys(result).length) {
				averageValue = averageValue / Object.keys(result).length;
			}
			/*
			 * Queremos evitar que cuando se cierra los ojos aumente constantemente el número de parpadeos.
			 * Para ello revisamos si el % de emoción es superior al valor de control e identificamos que tiene los ojos cerrados
			 * Si tiene los ojos cerrados y obtenemos un % de emoción inferior significa que los ha abierto.
			 * Si tenemos más de 3 mediciones con los ojos abierto tras tener una de ojos cerrados, contamos un parpadeo
			 */
			if (averageValue > minPercenteageBlink) {
				this.hasBlink = true;
			}
			if (this.hasBlink && averageValue < minPercenteageBlink) {
				this.countBlink++;
			}
			if (this.countBlink >= this.MIN_MEASURE_FOR_BLINK && this.hasBlink) {
				this.totalBlinks++;
				if (controls) {
					controls.totalBlinks = this.totalBlinks;
				}
				this.hasBlink = false;
				this.countBlink = 0;
			}

			// Evaluamos con 2 medidas
			if (averageValue > minPercenteageBlink) {
				this.hasBlink2 = true;
			}
			if (this.hasBlink2 && averageValue < minPercenteageBlink) {
				this.countBlink2++;
			}
			if (this.countBlink2 >= 2 && this.hasBlink2) {
				this.totalBlinksWith2++;
				if (controls) {
					controls.totalBlinksWith2 = this.totalBlinksWith2;
				}
				this.hasBlink2 = false;
				this.countBlink2 = 0;
			}

			// Evaluamos con 1 medidas
			if (averageValue > minPercenteageBlink) {
				this.hasBlink1 = true;
			}
			if (this.hasBlink1 && averageValue < minPercenteageBlink) {
				this.countBlink1++;
			}
			if (this.countBlink1 >= 1 && this.hasBlink1) {
				this.totalBlinksWith1++;
				if (controls) {
					controls.totalBlinksWith1 = this.totalBlinksWith1;
				}
				this.hasBlink1 = false;
				this.countBlink1 = 0;
			}
		}
	}

	// Media de puntuaciones del scoring de las pruebas de vida
	private getAverageLifeTestResult(lifeCheckConf: IPolConf): number {
		if (lifeCheckConf.middleFace?.scoring && lifeCheckConf.lifeTest?.length) {
			let sum: number = 0;
			sum += lifeCheckConf.middleFace.scoring.result ?? 0;
			lifeCheckConf.lifeTest.forEach((lt: ILifeTest) => (sum += lt.scoring.result ?? 0));
			// El +1 es por que se agrega la puntiación del centrado de la cara
			return sum / (lifeCheckConf.lifeTest.length + 1);
		}
		return 0;
	}

	// Media de parpadeos por
	private getAverageBlinks(totalBlinks: number, totalTime: number): number {
		/*
		 * Media de parpadeos por minuto delante de las pantallas: 7
		 * Media de parpadeos por minuto en la vida cotidiana: 15
		 */
		if (totalBlinks && totalTime) {
			return (totalBlinks * 60) / totalTime;
		}
		return 0;
	}

	// Obtener el score de emocion para cada prueba
	public getFaceBlendshapes(type: LIFE_TEST_ID, faceBlendshapes: Array<Classifications>): any {
		if (!faceBlendshapes.length) {
			return;
		}

		const lifeTest = {
			[LIFE_TEST_ID.SMILE]: () => {
				return {
					mouthSmileLeft: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'mouthSmileLeft' || category.categoryName === 'mouthSmileLeft',
					)?.score,
					mouthSmileRight: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'mouthSmileRight' || category.categoryName === 'mouthSmileRight',
					)?.score,
				};
			},
			[LIFE_TEST_ID.APPROACHFACE]: () => {
				return {};
			},
			[LIFE_TEST_ID.BLINK]: () => {
				return {
					eyeBlinkLeft: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeBlinkLeft' || category.categoryName === 'eyeBlinkLeft',
					)?.score,
					eyeBlinkRight: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeBlinkRight' || category.categoryName === 'eyeBlinkRight',
					)?.score,
				};
			},
			[LIFE_TEST_ID.TURNLEFTHEAD]: () => {
				return {
					eyeLookInLeft: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookInLeft' || category.categoryName === 'eyeLookInLeft',
					)?.score,
					eyeLookOutRight: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookOutRight' || category.categoryName === 'eyeLookOutRight',
					)?.score,
				};
			},
			[LIFE_TEST_ID.TURNRIGHTHEAD]: () => {
				return {
					eyeLookOutLeft: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookOutLeft' || category.categoryName === 'eyeLookOutLeft',
					)?.score,
					eyeLookInRight: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookInRight' || category.categoryName === 'eyeLookInRight',
					)?.score,
				};
			},
			[LIFE_TEST_ID.LIFTHEAD]: () => {
				return {
					eyeLookDownLeft: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookDownLeft' || category.categoryName === 'eyeLookDownLeft',
					)?.score,
					eyeLookDownRight: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookDownRight' || category.categoryName === 'eyeLookDownRight',
					)?.score,
				};
			},
			[LIFE_TEST_ID.LOWERHEAD]: () => {
				return {
					eyeLookUpLeft: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookUpLeft' || category.categoryName === 'eyeLookUpLeft',
					)?.score,
					eyeLookUpRight: faceBlendshapes[0].categories.find(
						(category: Category) =>
							category.displayName === 'eyeLookUpRight' || category.categoryName === 'eyeLookUpRight',
					)?.score,
				};
			},
		};
		return lifeTest[type]();
	}

	// Funcion auxiliar
	public getAverageArray(array: Array<any>): Array<number> {
		if (array.length) {
			const averageArray: Array<number> = [];
			array.forEach((emotions: any) => {
				if (emotions && Object.keys(emotions).length) {
					let averageValue: number = 0;
					for (const field of Object.keys(emotions)) {
						averageValue += emotions[field] * 100 || 0;
					}
					if (Object.keys(emotions).length) {
						averageValue = averageValue / Object.keys(emotions).length;
					}
					averageArray.push(averageValue);
				}
			});
			return averageArray;
		}
		return [];
	}

	private getLabelsPhotos(lifeCheckConf: IPolConf): string {
		let result: string = '';

		result += lifeCheckConf.middleFace?.photos?.map((p: { img: string; label: string }) => p.label).join() + ',';
		result += lifeCheckConf.lifeTest?.map((lt: ILifeTest) =>
			lt.photos.map((p: { img: string; label: string }) => p.label).join(),
		);

		return result;
	}

	private getAverageSimilarityPercent(): number {
		const s = this.similarity ?? ScoringFaceService.DEFAULT_SIMILARITY;
		return (s.environment + s.face) / 2;
	}

	/*
	 * Getter and setter
	 */
	// Similitud
	public getSimilarity(): ISimilarity {
		return this.similarity ?? ScoringFaceService.DEFAULT_SIMILARITY;
	}

	public setSimilarity(value: ISimilarity): void {
		this.similarity = JSON.parse(JSON.stringify(value));
	}

	// Número de caras simultaneas
	public getSimultaneousFaces(): number {
		return this.simultaneousFaces;
	}

	public setSimultaneousFaces(value: number): void {
		this.simultaneousFaces = value;
	}

	// Emotion
	// Prueba de vida
	public getLifeTestEmotion(): Array<number> {
		return this.lifeTestEmotion;
	}

	public setLifeTestEmotion(value: Array<number>): void {
		this.lifeTestEmotion = value;
	}

	public addLifeTestEmotion(value: number): void {
		this.lifeTestEmotion.push(value);
	}
	// Validación
	public getValidationEmotion(): Array<number> {
		return this.validationEmotion;
	}

	public setValidationEmotion(value: Array<number>): void {
		this.validationEmotion = value;
	}

	public addValidationEmotion(value: number): void {
		this.validationEmotion.push(value);
	}

	// Match
	// Prueba de vida
	public getLifeTestMatch(): Array<boolean> {
		return this.lifeTestMatch;
	}

	public setLifeTestMatch(value: Array<boolean>): void {
		this.lifeTestMatch = value;
	}

	public addLifeTestMatch(value: boolean): void {
		this.lifeTestMatch.push(value);
	}

	// Prueba completa
	public getFullTestMatch(): Array<boolean> {
		return this.fullTestMatch;
	}

	public setFullTestMatch(value: Array<boolean>): void {
		this.fullTestMatch = value;
	}

	public addFullTestMatch(value: boolean): void {
		this.fullTestMatch.push(value);
	}

	// Validación
	public getValidationMatch(): Array<boolean> {
		return this.validationMatch;
	}

	public setValidationMatch(value: Array<boolean>): void {
		this.validationMatch = value;
	}

	public addValidationMatch(value: boolean): void {
		this.validationMatch.push(value);
	}
}
